<template>
  <section class="travel-planner">
    <router-view></router-view>
  </section>
</template>

<script lang="ts">
import { defineComponent, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useJourneyStore } from '@/stores/journeyStore';
import { fetchAllStations } from '@/services/journeyService';
import { useI18n } from 'vue-i18n';

export default defineComponent({
  name: 'TravelPlanner',
  setup() {
    const route = useRoute();
    const journeyStore = useJourneyStore();
    const { locale } = useI18n({ useScope: 'global' });

    const initializeFromQuery = async () => {
      if (!journeyStore.allStations.length) {
        try {
          const stations = await fetchAllStations();
          journeyStore.setAllStations(stations);
        } catch (error) {
          console.error('Failed to fetch stations:', error);
          journeyStore.setInitialized(true);
          return;
        }
      }

      const { origin, destination, departureDate, returnDate, lang } = route.query;

      if (origin && typeof origin === 'string' && !journeyStore.OriginStation.name) {
        const station = journeyStore.allStations.find((s) => s._u_i_c_station_code === origin);
        if (station) {
          journeyStore.setOriginStation(station);
        } else {
          console.warn(`Origin station with UIC code ${origin} not found`);
        }
      }

      if (destination && typeof destination === 'string' && !journeyStore.destinationStation.name) {
        const station = journeyStore.allStations.find((s) => s._u_i_c_station_code === destination);
        if (station) {
          journeyStore.setDestinationStation(station);
        } else {
          console.warn(`Destination station with UIC code ${destination} not found`);
        }
      }

      if (departureDate && typeof departureDate === 'string') {
        journeyStore.setDepartureDate(departureDate);
      }

      if (returnDate && typeof returnDate === 'string') {
        journeyStore.setReturnDate(returnDate);
      }

      if (lang && typeof lang === 'string' && ['en', 'no'].includes(lang)) {
        locale.value = lang;
      } else if (lang) {
        console.warn(`Invalid language code: ${lang}. Supported: en, no`);
      }

      journeyStore.setInitialized(true);
    };

    onMounted(async () => {
      await initializeFromQuery();
    });

    watch(
      () => route.query,
      async () => {
        await initializeFromQuery();
      },
      { deep: true }
    );

    return {};
  },
});
</script>

<style lang="scss" scoped>
.travel-planner {
  display: flex;
  flex-direction: column;
  color: $clr-text;
  margin-top: 68px;
  margin-bottom: rem(120);
  padding-right: 16px;
  padding-left: 16px;

  @media screen and (min-width: $breakpoint-md) {
    margin-bottom: rem(80);
  }
}
</style>